import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { updateRespondentAssetAppeal } from "services/assetAppealService";
import { AssetAppeal } from "types";

interface ProsecutorResponsePopupProps {
  isOpen: boolean;
  onClose: () => void;
  appealId?: string;
  existingData?: {
    appeal_response_summary?: string;
    date_submitted?: string;
    prosecutor_response_file?: string;
  };
  onSuccess?: () => void;
  setAppeal: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
}

export default function ProsecutorResponsePopup({
  isOpen,
  onClose,
  appealId,
  existingData,
  onSuccess,
  setAppeal,
}: ProsecutorResponsePopupProps) {
  const [formData, setFormData] = useState({
    appeal_response_summary: "",
    date_submitted: "",
  });

  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { showTooltip } = useTooltip();

  // Pre-fill form with existing data when popup opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        appeal_response_summary: existingData?.appeal_response_summary || "",
        date_submitted: existingData?.date_submitted || "",
      });
      setUploadedFiles([]);
    }
  }, [isOpen, existingData]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.appeal_response_summary)
      return "Appeal Response Summary is required.";
    if (!formData.date_submitted) return "Date Submitted is required.";
    return "";
  };

  const handleClose = () => {
    setFormData({
      appeal_response_summary: "",
      date_submitted: "",
    });
    setUploadedFiles([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Prepare form data for submission
      const submitFormData = new FormData();

      // Add form fields
      submitFormData.append(
        "appeal_response_summary",
        formData.appeal_response_summary
      );
      submitFormData.append("date_submitted", formData.date_submitted);

      // Add appeal ID if provided
      if (appealId) {
        submitFormData.append("appeal_id", appealId);
      }

      // Add prosecutor response files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          submitFormData.append("prosecutor_response", file);
        });
      }
      // Simulate API call
      const response = await updateRespondentAssetAppeal(
        appealId as string,
        submitFormData
      );
      if (response) {
        showTooltip("Prosecutor response submitted successfully!", "success");
        handleClose();
        setAppeal?.(response.respondant ?? response);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error submitting prosecutor response:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to submit prosecutor response";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Check if we're in edit mode
  const isEditMode = !!existingData?.appeal_response_summary;

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditMode ? "Update Prosecutor Response" : "Submit Prosecutor Response"
      }
      description={
        isEditMode
          ? "Update the prosecutor's response to the appeal"
          : "Submit prosecutor's response to the appeal"
      }
      w="600px"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Appeal Response Summary */}
        <div className="flex flex-col">
          <label className="referral-popup-label">
            Appeal Response Summary
          </label>
          <textarea
            className="referral-form-field min-h-[120px] resize-vertical"
            name="appeal_response_summary"
            value={formData.appeal_response_summary}
            onChange={handleInputChange}
            placeholder="Provide detailed response summary for the appeal..."
            required
          />
        </div>

        {/* Date Submitted */}
        <div className="flex flex-col">
          <label className="referral-popup-label">Date Submitted</label>
          <input
            type="date"
            className="referral-form-field"
            name="date_submitted"
            value={formData.date_submitted}
            onChange={handleInputChange}
            required
          />
        </div>

        {/* Prosecutor Response File */}
        <MultipleFileUploadComponent
          uploadedFiles={uploadedFiles}
          onFilesChange={setUploadedFiles}
          label="Prosecutor Response Documents"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading
            ? isEditMode
              ? "Updating Response..."
              : "Submitting Response..."
            : isEditMode
            ? "Update Prosecutor Response"
            : "Submit Prosecutor Response"}
        </button>
      </form>
    </BasePopup>
  );
}
